// src/components/LegalContent.jsx
import React from 'react';

export const TermsContent = () => (
  <div className="prose prose-invert prose-blue max-w-none">
    <h2>Terms of Service</h2>
    <p className="text-sm text-slate-400">Last updated: December 14, 2024</p>

    <h3>1. Agreement to Terms</h3>
    <p>
      By accessing our website, you agree to be bound by these Terms of Service and to comply with all applicable laws and regulations. If you do not agree with these Terms, you should not use our website or services.
    </p>

    <h3>2. Intellectual Property Rights</h3>
    <p>
      All content on this website, including but not limited to text, graphics, logos, images, audio clips, digital downloads, and data compilations, is the property of Carter Case and Ned and Nancy or its content suppliers and is protected by international copyright laws.
    </p>

    <h3>3. User Account Responsibilities</h3>
    <p>
      If you create an account on our website, you are responsible for maintaining the confidentiality of your account and password. You agree to accept responsibility for all activities that occur under your account.
    </p>

    <h3>4. Acceptable Use Policy</h3>
    <p>When using our website, you agree not to:</p>
    <ul>
      <li>Violate any applicable laws or regulations</li>
      <li>Infringe upon the rights of others</li>
      <li>Distribute harmful or malicious content</li>
      <li>Attempt to gain unauthorized access to our systems</li>
      <li>Interfere with the proper functioning of the website</li>
    </ul>

    <h3>5. Content Guidelines</h3>
    <p>
      User-generated content must be appropriate for all audiences and comply with our community guidelines. We reserve the right to remove any content that violates these terms.
    </p>

    <h3>6. Purchase Terms</h3>
    <p>
      All purchases made through our website are subject to our refund and return policies. Physical books can be returned within 30 days of purchase. Digital products, once delivered, are non-refundable unless otherwise specified.
    </p>
    <p>
      Shipping costs for physical products are non-refundable. Customers are responsible for return shipping costs unless the return is due to our error.
    </p>

    <h3>7. Subscription Services</h3>
    <p>
      For any subscription-based services:
    </p>
    <ul>
      <li>Subscriptions automatically renew unless cancelled before the renewal date</li>
      <li>You can cancel your subscription at any time through your account settings</li>
      <li>Refunds are not provided for partial subscription periods</li>
    </ul>

    <h3>8. Limitation of Liability</h3>
    <p>
      We shall not be liable for any indirect, incidental, special, consequential, or punitive damages resulting from your use or inability to use our services.
    </p>

    <h3>9. Indemnification</h3>
    <p>
      You agree to indemnify and hold harmless Carter Case, Ned and Nancy, its affiliates, officers, directors, employees, and agents from any claims, damages, losses, liabilities, costs, or expenses arising from your use of our services or violation of these Terms.
    </p>

    <h3>10. Changes to Terms</h3>
    <p>
      We reserve the right to modify these terms at any time. Continued use of the website after changes constitutes acceptance of the modified terms. We will notify users of significant changes via email or website notification.
    </p>

    <h3>11. Governing Law</h3>
    <p>
      These Terms shall be governed by and construed in accordance with the laws of the jurisdiction in which we operate, without regard to its conflict of law provisions.
    </p>

    <h3>12. Contact Information</h3>
    <p>
      For questions about these Terms, please contact us through our provided contact methods on the Contact page.
    </p>
  </div>
);

export const PrivacyContent = () => (
  <div className="prose prose-invert prose-blue max-w-none">
    <h2>Privacy Policy</h2>
    <p className="text-sm text-slate-400">Last updated: December 14, 2024</p>

    <h3>1. Information We Collect</h3>
    <h4>1.1 Personal Information</h4>
    <p>We may collect the following personal information:</p>
    <ul>
      <li>Name and contact information</li>
      <li>Email address</li>
      <li>Billing and shipping information</li>
      <li>Account login credentials</li>
      <li>Purchase history and preferences</li>
      <li>Communication preferences</li>
    </ul>

    <h4>1.2 Automatically Collected Information</h4>
    <p>We automatically collect certain information when you visit our website:</p>
    <ul>
      <li>IP address and device information</li>
      <li>Browser type and settings</li>
      <li>Website usage data</li>
      <li>Cookies and similar technologies</li>
      <li>Geographic location (with consent)</li>
      <li>Referral source</li>
    </ul>

    <h3>2. How We Use Your Information</h3>
    <p>We use your information to:</p>
    <ul>
      <li>Process your orders and transactions</li>
      <li>Send newsletters and marketing communications</li>
      <li>Improve our website and services</li>
      <li>Comply with legal obligations</li>
      <li>Prevent fraud and enhance security</li>
      <li>Personalize your experience</li>
      <li>Respond to your inquiries and support requests</li>
      <li>Analyze usage patterns and trends</li>
    </ul>

    <h3>3. Information Sharing</h3>
    <p>We may share your information with:</p>
    <ul>
      <li>Service providers and business partners</li>
      <li>Payment processors</li>
      <li>Shipping and fulfillment partners</li>
      <li>Legal authorities when required by law</li>
      <li>Professional advisers and insurers</li>
      <li>Successor entities in case of business transfer</li>
    </ul>

    <h3>4. Data Security</h3>
    <p>
      We implement appropriate technical and organizational measures to protect your personal information. These measures include:
    </p>
    <ul>
      <li>Encryption of sensitive data</li>
      <li>Regular security assessments</li>
      <li>Access controls and authentication</li>
      <li>Secure data storage and transmission</li>
      <li>Regular staff training on data protection</li>
    </ul>

    <h3>5. Your Rights</h3>
    <p>You have the right to:</p>
    <ul>
      <li>Access your personal information</li>
      <li>Correct inaccurate data</li>
      <li>Request deletion of your data</li>
      <li>Opt-out of marketing communications</li>
      <li>Object to processing of your information</li>
      <li>Request data portability</li>
      <li>Withdraw consent at any time</li>
    </ul>

    <h3>6. Cookies Policy</h3>
    <p>
      We use cookies and similar tracking technologies to improve your browsing experience. You can control cookie settings through your browser preferences. We use the following types of cookies:
    </p>
    <ul>
      <li>Essential cookies for website functionality</li>
      <li>Analytics cookies to understand usage</li>
      <li>Preference cookies to remember your settings</li>
      <li>Marketing cookies for targeted advertising</li>
    </ul>

    <h3>7. Children's Privacy</h3>
    <p>
      Our services are not directed to children under 13. We do not knowingly collect personal information from children under 13. If we learn we have collected personal information from a child under 13, we will take steps to delete such information.
    </p>

    <h3>8. International Data Transfers</h3>
    <p>
      Your information may be transferred to and processed in countries other than your own. We ensure appropriate safeguards are in place for such transfers through standard contractual clauses or other legal mechanisms.
    </p>

    <h3>9. Changes to Privacy Policy</h3>
    <p>
      We may update this privacy policy from time to time. We will notify you of any changes by posting the new policy on this page and updating the "Last updated" date. Significant changes will be notified via email.
    </p>

    <h3>10. Data Retention</h3>
    <p>
      We retain your personal information for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required by law.
    </p>

    <h3>11. Contact Us</h3>
    <p>
      If you have questions about this Privacy Policy or our data practices, please contact us through our provided contact methods on the Contact page.
    </p>
  </div>
);